import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  X,
  Sun,
  BookOpen,
  Trophy,
  Users,
  Star,
} from 'lucide-react';

const EventsPage = () => {
  // State management
  const [activeTab, setActiveTab] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEventType, setSelectedEventType] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'meeting',
    startDate: '',
    endDate: '',
    isHoliday: false,
    forClasses: '',
  });

  // Color mapping for event types
  const eventTypeColors = {
    holiday: '#ef4444',
    exam: '#a855f7',
    meeting: '#3b82f6',
    sports: '#22c55e',
    cultural: '#f472b6',
    other: '#6b7280',
  };

  const eventTypeIcons = {
    holiday: Sun,
    exam: BookOpen,
    meeting: Users,
    sports: Trophy,
    cultural: Star,
    other: Calendar,
  };

  // Fetch events based on active tab
  useEffect(() => {
    fetchEvents();
  }, [activeTab, selectedMonth]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/events';

      if (activeTab === 'holidays') {
        url = '/events/holidays';
      } else if (activeTab === 'exams') {
        url = '/events?eventType=exam';
      } else if (activeTab === 'upcoming') {
        url = '/events/upcoming';
      }

      const response = await api.get(url);
      const data = response.data.events || response.data;
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      eventType: 'meeting',
      startDate: '',
      endDate: '',
      isHoliday: false,
      forClasses: '',
    });
    setShowModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingId(event.id);
    setFormData({
      title: event.title,
      description: event.description || '',
      eventType: event.eventType || 'meeting',
      startDate: event.startDate,
      endDate: event.endDate || '',
      isHoliday: event.isHoliday || false,
      forClasses: event.forClasses ? event.forClasses.join(', ') : '',
    });
    setShowModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      setEvents(events.filter((e) => e.id !== eventId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event');
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        forClasses: formData.forClasses
          ? formData.forClasses.split(',').map((c) => c.trim()).filter((c) => c)
          : [],
      };

      if (editingId) {
        await api.put(`/events/${editingId}`, payload);
      } else {
        await api.post('/events', payload);
      }

      setShowModal(false);
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
      setError('Failed to save event');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getNextUpcomingEvent = () => {
    if (events.length === 0) return null;
    const today = new Date();
    const upcoming = events.filter((e) => new Date(e.startDate) >= today);
    return upcoming.length > 0 ? upcoming[0] : null;
  };

  const getHolidayCount = () => {
    return events.filter((e) => e.isHoliday || e.eventType === 'holiday').length;
  };

  const filterEventsByMonth = (eventsToFilter) => {
    if (!selectedMonth) return eventsToFilter;
    return eventsToFilter.filter((event) => {
      const eventMonth = event.startDate.slice(0, 7);
      return eventMonth === selectedMonth;
    });
  };

  const filterEventsByType = (eventsToFilter) => {
    if (!selectedEventType) return eventsToFilter;
    return eventsToFilter.filter((event) => event.eventType === selectedEventType);
  };

  const filteredEvents = filterEventsByType(filterEventsByMonth(events));
  const nextEvent = getNextUpcomingEvent();
  const holidayCount = getHolidayCount();

  const EventCard = ({ event }) => {
    const IconComponent = eventTypeIcons[event.eventType] || Calendar;
    const borderColor = eventTypeColors[event.eventType] || eventTypeColors.other;

    return (
      <div
        style={{
          borderLeft: `4px solid ${borderColor}`,
          padding: '16px',
          marginBottom: '12px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <IconComponent size={18} style={{ color: borderColor }} />
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {event.title}
              </h3>
            </div>

            {event.description && (
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                {event.description}
              </p>
            )}

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                📅 {formatDate(event.startDate)}
              </span>
              {event.endDate && event.endDate !== event.startDate && (
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                  to {formatDate(event.endDate)}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
              <span
                className="badge"
                style={{
                  backgroundColor: borderColor,
                  color: '#ffffff',
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textTransform: 'capitalize',
                }}
              >
                {event.eventType}
              </span>
              {event.isHoliday && (
                <span
                  className="badge"
                  style={{
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  Holiday
                </span>
              )}
              {event.forClasses && event.forClasses.length > 0 && (
                <span
                  className="badge"
                  style={{
                    backgroundColor: '#d4a053',
                    color: '#ffffff',
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  Class: {event.forClasses.join(', ')}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
            <button
              onClick={() => handleEditEvent(event)}
              className="btn btn-secondary btn-sm"
              style={{
                padding: '6px 10px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => setDeleteConfirm(event.id)}
              className="btn btn-danger btn-sm"
              style={{
                padding: '6px 10px',
                backgroundColor: '#fee2e2',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Trash2 size={16} style={{ color: '#dc2626' }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'all', label: 'All Events' },
    { id: 'holidays', label: 'Holidays' },
    { id: 'exams', label: 'Exams' },
    { id: 'upcoming', label: 'Upcoming' },
  ];

  const eventTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'exam', label: 'Exam' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'sports', label: 'Sports' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Events & Holidays
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage events, holidays, and exam dates</p>
        </div>

        {/* Summary Bar */}
        <div
          className="card"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '16px',
          }}
        >
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Total Events
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              {events.length}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Holidays
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>
              {holidayCount}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Next Event
            </p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#111827' }}>
              {nextEvent ? nextEvent.title : 'None'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            borderBottom: '1px solid #e5e7eb',
            marginBottom: '24px',
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '500',
                color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : 'none',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div
          className="filter-bar"
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="form-control"
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />

          <select
            value={selectedEventType}
            onChange={(e) => setSelectedEventType(e.target.value)}
            className="form-control"
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            {eventTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleAddEvent}
            className="btn btn-primary"
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: 'auto',
            }}
          >
            <Plus size={18} />
            Add Event
          </button>
        </div>

        {/* Events Display */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading events...
            </div>
          ) : filteredEvents.length === 0 ? (
            <div
              className="empty-state"
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6b7280',
              }}
            >
              <Calendar size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No events found</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {selectedMonth && selectedEventType
                  ? 'Try adjusting your filters'
                  : 'Add your first event to get started'}
              </p>
            </div>
          ) : (
            <div>
              {filteredEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="modal"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-body"
              style={{
                padding: '24px',
              }}
            >
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Event?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this event?
              </p>
            </div>
            <div
              className="modal-footer"
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn btn-secondary"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEvent(deleteConfirm)}
                className="btn btn-danger"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Event Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Event' : 'Add New Event'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveEvent}>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Event Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter event title"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter event description (optional)"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Event Type
                    </label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleFormChange}
                      className="form-control"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="meeting">Meeting</option>
                      <option value="exam">Exam</option>
                      <option value="holiday">Holiday</option>
                      <option value="sports">Sports</option>
                      <option value="cultural">Cultural</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleFormChange}
                      className="form-control"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      End Date (optional)
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleFormChange}
                      className="form-control"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isHoliday"
                        checked={formData.isHoliday}
                        onChange={handleFormChange}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>Is Holiday</span>
                    </label>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    For Classes (comma-separated, e.g., "A, B, C")
                  </label>
                  <input
                    type="text"
                    name="forClasses"
                    value={formData.forClasses}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Leave empty for all classes"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                className="modal-footer"
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
